/**
 * Pipe-separated wire format `id|displayName|entityType|subType` used to
 * persist a {@link EntityLink} into URL search params and back. Only the
 * `id` half is meaningful to the backend filter parameters (typed `UUID`
 * or `List<UUID>`); the trailing parts exist purely so the chip / picker
 * can re-render with a real displayName after a URL refresh, without an
 * extra `findLinks` round-trip.
 *
 * Single source of truth — both {@link FilterPanel} (writes the packed
 * value into URL state) and {@link buildFilterQuery} (strips back to id
 * before the HTTP call) MUST use this constant.
 */
export const ENTITY_LINK_PART_SEPARATOR = '|'

/**
 * Strip a packed `id|displayName|entityType|subType` value down to the bare
 * id. Values that do not contain the separator pass through unchanged —
 * hand-crafted URLs carrying just the id keep working, and any non-picker
 * uuid value (e.g., a literal UUID typed into the manual-entry sentinel
 * box) is left alone.
 */
export function unpackEntityLinkId(value: string): string {
    const sep = value.indexOf(ENTITY_LINK_PART_SEPARATOR)
    if (sep < 0) {
        return value
    }
    return decodeURIComponent(value.slice(0, sep))
}

/**
 * Strip every packed element of a CSV-of-UUID multi-select value back to a
 * comma-joined list of bare ids. Each comma-separated chunk is treated as
 * its own packed value; chunks without the separator pass through.
 */
export function unpackEntityLinkIdsCsv(value: string): string {
    return value.split(',')
        .map((part) => unpackEntityLinkId(part))
        .join(',')
}

/**
 * Read the displayName slice out of a packed `id|displayName|entityType|subType`
 * value. Returns the input unchanged when the value is bare (no separator) or
 * when the displayName slice is empty — chip / pill renderers fall back to
 * the bare id in that case.
 */
export function unpackEntityLinkDisplayName(value: string): string {
    const sep = value.indexOf(ENTITY_LINK_PART_SEPARATOR)
    if (sep < 0) {
        return value
    }
    const after = value.slice(sep + 1)
    const next = after.indexOf(ENTITY_LINK_PART_SEPARATOR)
    const encoded = next < 0 ? after : after.slice(0, next)
    if (encoded.length === 0) {
        return decodeURIComponent(value.slice(0, sep))
    }
    try {
        return decodeURIComponent(encoded)
    } catch (_decodeError) {
        // WHY: malformed % sequences in a hand-edited URL must not crash
        // the chip render — return the raw slice so the user still sees
        // something recognisable instead of a thrown exception.
        return encoded
    }
}
